import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Paper, Box, Stack } from '@mui/material';
import { TableContainer, Table, TableHead, TableBody, TableRow } from '@mui/material';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';

import ActSchedule from '@/types/ActSchedule.type';
import URL from '@/common/routes/Url';
import dayjs from 'dayjs';
import ActPlanIcon from '@/components/actPlan/ActPlanIcon';

type Props = {
  actSchedule: Array<ActSchedule>;
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: '1px 10px',
  fontSize: 14,
  border: '1px solid #E2E7EA',
}));

const StyledTableCellHead = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#ced7dc',
    color: '#3e3a3a',
    fontSize: 15,
    fontWeight: 'bold',
    borderLeft: '1px solid #E2E7EA',
    borderBottom: '2px solid #E2E7EA',
    padding: '3px 10px',
  },
  [`&.${tableCellClasses.body}`]: {
    padding: '1px 10px',
    fontSize: 14,
    borderLeft: '1px solid #E2E7EA',
    borderBottom: '2px solid #E2E7EA',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: '#F3F5F7',
  },
}));

const ActPlanTable: React.FC<Props> = ({ actSchedule }) => {
  const navigate = useNavigate();

  const nextDate = (undecidedFlag: boolean, date: string | null) => {
    if (undecidedFlag) {
      return '未定';
    } else if (date) {
      return dayjs(date).format('MM/DD');
    }
    return '';
  };

  return (
    <TableContainer component={Paper}>
      <Table size="small" sx={{ minWidth: 900 }}>
        <TableHead>
          <TableRow>
            <StyledTableCellHead align="left" style={{ width: '9rem' }}>
              氏名
            </StyledTableCellHead>
            <StyledTableCellHead align="left" style={{ width: '8rem' }}>
              ステータス
            </StyledTableCellHead>
            <StyledTableCellHead align="left">行先</StyledTableCellHead>
            <StyledTableCellHead align="center" style={{ width: '6rem' }}>
              戻り時間
            </StyledTableCellHead>
            <StyledTableCellHead align="center" style={{ width: '7rem' }}>
              次回出社日
            </StyledTableCellHead>
            <StyledTableCellHead align="center" style={{ width: '9rem' }}>
              連絡先
            </StyledTableCellHead>
            <StyledTableCellHead align="center" style={{ width: '7rem' }}>
              更新日時
            </StyledTableCellHead>
          </TableRow>
        </TableHead>
        <TableBody>
          {actSchedule.map((row, index) => (
            <StyledTableRow key={index}>
              {/* 氏名 */}
              <StyledTableCell
                sx={{
                  backgroundColor: row.style.color,
                }}
              >
                <Link onClick={() => navigate(`${URL.plan}/${row.id}`)} color="#000000" style={{ cursor: 'pointer' }}>
                  <b>{row.employee.name}</b>
                </Link>
                {(row.startTime || row.endTime) && (
                  <Typography variant="subtitle1" sx={{ fontSize: 12 }}>
                    勤務時間|
                    <b>
                      {row.startTime}~{row.endTime}
                    </b>
                  </Typography>
                )}
              </StyledTableCell>
              {/* ステータス */}
              <StyledTableCell>
                <Stack direction="row">
                  <Box width={'4rem'} sx={{ fontStyle: 'bold', display: 'flex', alignItems: 'center' }}>
                    {row.status.displayName}
                  </Box>
                  <ActPlanIcon actSchedule={row} />
                </Stack>
              </StyledTableCell>
              {/* 行き先 */}
              <StyledTableCell align="left">
                <Box style={{ whiteSpace: 'pre-wrap' }}>{row.place}</Box>
              </StyledTableCell>
              {/* 戻り時間 */}
              <StyledTableCell
                align="center"
                sx={
                  !row.goingStraightHomeFlag && row.returnTime && dayjs().format('HH:mm') > row.returnTime
                    ? { backgroundColor: '#ff8a80' }
                    : null
                }
              >
                {row.goingStraightHomeFlag ? '直帰' : row.returnTime}
              </StyledTableCell>
              {/* 次回出社日 */}
              <StyledTableCell
                align="center"
                sx={{
                  ...(!row.undecidedFlag && row.nextDate && dayjs() > dayjs(row.nextDate)
                    ? { backgroundColor: '#ff8a80' }
                    : {}),
                }}
              >
                {nextDate(row.undecidedFlag, row.nextDate)}
              </StyledTableCell>
              {/* 連絡先 */}
              <StyledTableCell align="center">
                {row.phone ? (
                  row.phone.number
                ) : (
                  <Stack>
                    <Box>{row.employee.extensionNumber}</Box>
                    <Box>{row.employee.phoneNumber}</Box>
                  </Stack>
                )}
              </StyledTableCell>
              <StyledTableCell align="center">{row.updatedAt}</StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
export default ActPlanTable;
